import { useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db, clearLocalData } from "@/lib/local/db";
import { deriveKeyFromPassword, hasSessionKey, clearSessionKey } from "@/lib/local/crypto";
import { useFiscalStore } from "@/store/fiscal-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Lock, Trash2, ShieldCheck,
  Database, HardDrive, TrendingUp, Zap, Activity
} from "lucide-react";
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, 
  CartesianGrid, Tooltip as RechartsTooltip 
} from "recharts";
import { cn } from "@/lib/utils";

export default function DashboardLocalPage() {
  const [pwd, setPwd] = useState("");
  const [hasKey, setHasKey] = useState(hasSessionKey());
  const userId = useFiscalStore(s => s.userId);

  useEffect(() => { document.title = "Núcleo Offline — XMLs em larga escala"; }, []);

  // Live count filtrado por usuário
  const total = useLiveQuery(
    () => userId ? db.notas.where("userId").equals(userId).count() : 0, 
    [userId], 
    0
  );

  const [agg, setAgg] = useState<{ growth: { date: string, count: number }[] }>({ growth: [] });
  const [storage, setStorage] = useState<{ used: number, total: number }>({ used: 0, total: 0 });
  const [optimizing, setOptimizing] = useState(false);
  const [statsInfra, setStatsInfra] = useState({ avgSize: 0 });

  useEffect(() => {
    if ("storage" in navigator && "estimate" in navigator.storage) {
      navigator.storage.estimate().then(est => {
        setStorage({ used: est.usage || 0, total: est.quota || 0 });
      });
    }
  }, [total]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const growthMap = new Map<string, number>();
      await db.notas
        .where("userId").equals(userId)
        .each((n) => {
          const d = n.data || "Indefinido";
          growthMap.set(d, (growthMap.get(d) || 0) + 1);
        });

      const growth = Array.from(growthMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(-30); 

      if (!cancelled) setAgg({ growth });
    })();
    return () => { cancelled = true; };
  }, [total, userId]);

  useEffect(() => {
    if (total > 0 && storage.used > 0) {
      setStatsInfra({
        avgSize: (storage.used / total) / 1024
      });
    }
  }, [total, storage.used]);

  async function handleOptimize() {
    setOptimizing(true);
    try {
      const { analyticsCache } = await import("@/services/repositories/analytics-cache");
      await analyticsCache.clear();
      await new Promise(r => setTimeout(r, 1500));
      toast.success("Banco de dados otimizado.", { description: "Caches limpos e índices re-sincronizados." });
    } catch (e) {
      toast.error("Erro na otimização.");
    } finally {
      setOptimizing(false);
    }
  }

  async function handleSetPassword() {
    if (pwd.length < 6) return alert("Use ao menos 6 caracteres.");
    await deriveKeyFromPassword(pwd);
    setHasKey(true);
    setPwd("");
  }

  async function handleClear() {
    if (!window.confirm("Apagar todos os SEUS XMLs locais? Esta ação é irreversível.")) return;
    if (!userId) return;

    // Remove apenas os dados do usuário atual
    await Promise.all([
      db.notas.where("userId").equals(userId).delete(),
      db.itens.where("userId").equals(userId).delete(),
      db.analyticsCache.where("userId").equals(userId).delete(),
    ]);

    clearSessionKey();
    setHasKey(false);
    toast.success("Seus dados locais foram apagados.");
  }

  const syncProgress = useFiscalStore(s => s.syncProgress);
  const lastSyncTime = useFiscalStore(s => s.lastSyncTime);
  const { growth } = agg;

  return (
    <div className="px-4 lg:px-6 py-4 lg:py-6 max-w-[1400px] mx-auto space-y-4 animate-in fade-in duration-300">
      <header className="flex flex-col gap-2">
        <h1 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-50">
          Núcleo Offline — XMLs em larga escala
        </h1>
        <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
          <ShieldCheck className="size-4" />
          <span>
            Gestão local isolada por usuário. Seus dados estão seguros e inacessíveis para outros logins.
          </span>
        </div>
      </header>

      {/* Criptografia e Ações Globais */}
      <div className="glass-card p-4 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Lock className="size-5 text-primary" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900 dark:text-white">
              Criptografia AES-GCM {hasKey ? "ativa" : "desativada"}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              Usuário: {userId ? `ID ${userId.slice(0, 8)}...` : "Não identificado"}
            </div>
          </div>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <Input
            type="password"
            placeholder={hasKey ? "Senha definida" : "Senha (mín. 6)"}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="md:w-56"
            disabled={hasKey}
          />
          {!hasKey ? (
            <Button onClick={handleSetPassword} size="sm">Ativar</Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => { clearSessionKey(); setHasKey(false); }}>
              Encerrar
            </Button>
          )}
          <Button variant="destructive" size="sm" onClick={handleClear} className="gap-2">
            <Trash2 className="size-4" /> Limpar Meus Dados
          </Button>
        </div>
      </div>

      {/* Banner de Sincronização Ativa */}
      {syncProgress && (
        <div className="glass-card border-emerald-500/30 bg-emerald-500/5 p-4 animate-in slide-in-from-top-4 duration-500">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="size-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Activity className="size-4 text-emerald-500 animate-spin" />
              </div>
              <div>
                <div className="text-xs font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                  Sincronização em Curso
                </div>
                <div className="text-[10px] text-muted-foreground font-bold uppercase">
                  Escaneando base local para o perfil selecionado...
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-black tabular-nums text-emerald-600 dark:text-emerald-400">
                {((syncProgress.current / syncProgress.total) * 100).toFixed(1)}%
              </div>
              <div className="text-[9px] font-black opacity-40 uppercase tracking-tighter">
                {syncProgress.current.toLocaleString()} / {syncProgress.total.toLocaleString()} XMLs
              </div>
            </div>
          </div>
          <div className="h-1.5 w-full bg-emerald-500/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 transition-all duration-300 ease-out shadow-[0_0_10px_rgba(16,185,129,0.5)]" 
              style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }} 
            />
          </div>
        </div>
      )}

      {/* KPIs de Infraestrutura */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="glass-card p-4 flex flex-col gap-1 border-emerald-500/10 relative overflow-hidden">
          {syncProgress && syncProgress.total > 0 && (
            <div className="absolute bottom-0 left-0 h-1 bg-emerald-500 transition-all duration-300" style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }} />
          )}
          <div className="flex items-center justify-between">
            <Activity className={cn("size-4 text-emerald-500", syncProgress ? "animate-spin" : "animate-pulse")} />
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">Status de Leitura</span>
          </div>
          <div className="text-xl font-black text-emerald-500 uppercase">
            {syncProgress ? "Lendo Banco..." : "Conectado"}
          </div>
          <div className="text-[10px] text-muted-foreground uppercase flex flex-col gap-1">
            <div className="flex justify-between">
              <span>{syncProgress ? "Processado:" : "Sincronizado:"}</span>
              <span className="font-bold">{syncProgress ? `${syncProgress.current.toLocaleString()} / ${syncProgress.total.toLocaleString()}` : (lastSyncTime || "—")}</span>
            </div>
            {syncProgress && syncProgress.total > 0 && (
              <div className="text-[8px] font-black text-emerald-600">
                {((syncProgress.current / syncProgress.total) * 100).toFixed(1)}% CONCLUÍDO
              </div>
            )}
          </div>
        </div>

        <div className="glass-card p-4 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <Database className="size-4 text-primary" />
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">Volume Pessoal</span>
          </div>
          <div className="text-xl font-black tabular-nums">{(total ?? 0).toLocaleString("pt-BR")}</div>
          <div className="text-[10px] text-muted-foreground uppercase">Notas no Seu Perfil</div>
        </div>

        <div className="glass-card p-4 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <HardDrive className="size-4 text-emerald-500" />
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">Ocupação</span>
          </div>
          <div className="text-xl font-black tabular-nums">{(storage.used / 1024 / 1024).toFixed(1)} MB</div>
          <div className="text-[10px] text-muted-foreground uppercase">Total no Browser</div>
        </div>

        <div className="glass-card p-4 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <Activity className="size-4 text-blue-500" />
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">Eficiência</span>
          </div>
          <div className="text-xl font-black tabular-nums">{statsInfra.avgSize.toFixed(1)} KB</div>
          <div className="text-[10px] text-muted-foreground uppercase">Tamanho Médio / Nota</div>
        </div>

        <div className="glass-card p-4 flex flex-col gap-1 group cursor-pointer border-amber-500/20" onClick={handleOptimize}>
          <div className="flex items-center justify-between">
            {optimizing ? <Activity className="size-4 text-amber-500 animate-spin" /> : <Zap className="size-4 text-amber-500" />}
            <span className="text-[9px] font-black uppercase tracking-widest text-amber-600">Manutenção</span>
          </div>
          <div className="text-xl font-black text-amber-600 uppercase truncate">
            {optimizing ? "Otimizando..." : "Otimizar"}
          </div>
          <div className="text-[10px] text-amber-600/70 uppercase">Limpeza do Seu Cache</div>
        </div>
      </div>

      {/* Gráfico de Crescimento */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest flex items-center gap-2 text-muted-foreground">
              <TrendingUp className="size-4 text-primary" /> Seu Crescimento de Dados
            </h2>
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1">Histórico de Ingestão do Seu Usuário</p>
          </div>
        </div>
        
        <div className="h-[250px] w-full">
          {growth.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-[10px] font-bold uppercase tracking-widest opacity-30 text-center px-10">
              Aguardando dados para análise temporal...
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={growth}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-5" />
                <XAxis 
                  dataKey="date" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 9, fontWeight: 700 }}
                  tickFormatter={(v) => v.split('-').reverse().slice(0, 2).join('/')}
                />
                <YAxis hide />
                <RechartsTooltip 
                  contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: 'bold' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  name="Volume"
                  stroke="#3B82F6" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorCount)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
